/**
 * Single source of truth for the product page's purchase state (selected
 * options + quantity), driving the buy box's variant pills, quantity
 * stepper, price, and Add to cart button.
 *
 * Variant matching runs against the real product JSON embedded by the
 * section (script[data-flv-product-json]), not a guess — every price/
 * availability update reflects the actual matched Shopify variant.
 */
import { CartUpdateEvent, CartErrorEvent, VariantUpdateEvent } from '@theme/events';
import { formatMoney as formatMinorUnits } from '@theme/money-formatting';

const root = document.querySelector('[data-flv-product-root]');
/** @type {HTMLScriptElement | null} */
const jsonScript = document.querySelector('[data-flv-product-json]');

if (root && jsonScript) {
  const product = JSON.parse(jsonScript.textContent ?? '{}');
  const moneyFormat = jsonScript.dataset.moneyFormat || '${{amount}}';
  const currency = jsonScript.dataset.currency || 'USD';
  const optionCount = product.options_with_values?.length ?? product.options?.length ?? 0;

  /** @type {string[]} */
  let selectedOptions = (product.selected_or_first_available_variant?.options ?? product.variants[0].options).slice();
  let quantity = 1;
  // Tracks the last variant we synced the gallery/URL to, so quantity-only
  // re-renders don't re-fire a variant change (and re-scroll the gallery).
  let lastSyncedVariantId = (product.selected_or_first_available_variant?.id ?? product.variants[0].id);

  const findVariant = () =>
    product.variants.find(
      (/** @type {{ options: string[] }} */ variant) =>
        selectedOptions.every((value, index) => variant.options[index] === value)
    );

  /** @param {number} cents */
  const formatMoney = (cents) => formatMinorUnits(cents, moneyFormat, currency);

  /** @param {{ price: number, compare_at_price: number | null }} variant */
  const renderPrice = (variant) => {
    const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
    // Floored (never rounded up) so the badge never overstates the real discount.
    const discountPercent = onSale
      ? Math.floor(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100)
      : 0;
    const badge = discountPercent > 0 ? `<span class="flv-discount-badge">-${discountPercent}% OFF</span>` : '';
    document.querySelectorAll('[data-flv-price]').forEach((el) => {
      el.innerHTML = onSale
        ? `<span class="flv-product__price-sale">${formatMoney(variant.price)}</span><span class="flv-product__price-compare">${formatMoney(/** @type {number} */ (variant.compare_at_price))}</span>${badge}`
        : `<span>${formatMoney(variant.price)}</span>`;
    });
  };

  const render = () => {
    const variant = findVariant();

    document.querySelectorAll('[data-flv-option-value]').forEach((el) => {
      const btn = /** @type {HTMLElement} */ (el);
      const index = Number(btn.dataset.optionIndex);
      btn.classList.toggle('flv-product__pill--active', btn.dataset.value === selectedOptions[index]);
    });

    document.querySelectorAll('[data-flv-qty-input]').forEach((el) => {
      /** @type {HTMLInputElement} */ (el).value = String(quantity);
    });

    if (!variant) return;

    renderPrice(variant);

    document.querySelectorAll('[data-flv-variant-input]').forEach((el) => {
      /** @type {HTMLInputElement} */ (el).value = String(variant.id);
    });

    document.querySelectorAll('[data-flv-add-to-cart]').forEach((el) => {
      const btn = /** @type {HTMLButtonElement} */ (el);
      btn.disabled = !variant.available;
      const label = btn.querySelector('[data-flv-add-label]');
      if (label) label.textContent = variant.available ? 'Add to cart' : 'Sold out';
    });

    if (variant.id !== lastSyncedVariantId) {
      lastSyncedVariantId = variant.id;

      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(variant.id));
      window.history.replaceState({}, '', url);

      document.dispatchEvent(
        new VariantUpdateEvent(variant, String(variant.id), { html: null, productId: product.id })
      );
    }
  };

  if (optionCount > 0) {
    document.addEventListener('click', (event) => {
      const btn = /** @type {HTMLElement} */ (event.target).closest('[data-flv-option-value]');
      if (!(btn instanceof HTMLElement)) return;
      selectedOptions[Number(btn.dataset.optionIndex)] = btn.dataset.value ?? '';
      render();
    });
  }

  document.addEventListener('click', (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target.closest('[data-flv-qty-increase]')) {
      quantity += 1;
      render();
    } else if (target.closest('[data-flv-qty-decrease]')) {
      quantity = Math.max(1, quantity - 1);
      render();
    }
  });

  document.addEventListener('change', (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (!target.matches('[data-flv-qty-input]')) return;
    quantity = Math.max(1, parseInt(/** @type {HTMLInputElement} */ (target).value, 10) || 1);
    render();
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-flv-product-form]')) return;

    event.preventDefault();

    const variant = findVariant();
    if (!variant || !variant.available) return;

    const buttons = document.querySelectorAll('[data-flv-add-to-cart]');
    buttons.forEach((el) => {
      const btn = /** @type {HTMLButtonElement} */ (el);
      btn.disabled = true;
      const label = /** @type {HTMLElement | null} */ (btn.querySelector('[data-flv-add-label]'));
      if (label) {
        label.dataset.originalText = label.textContent ?? '';
        label.textContent = 'Adding…';
      }
    });

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'application/json' },
      });
      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.description || result.message || 'Add to cart failed');
      }

      const cart = await fetch('/cart.js').then((res) => res.json());

      document.dispatchEvent(
        new CartUpdateEvent(cart, String(variant.id), {
          source: 'furlouve-product-buy',
          productId: product.id,
          itemCount: cart.item_count,
          variantId: variant.id,
        })
      );

      buttons.forEach((el) => {
        const label = el.querySelector('[data-flv-add-label]');
        if (label) label.textContent = 'Added!';
      });
    } catch (error) {
      buttons.forEach((el) => {
        const label = el.querySelector('[data-flv-add-label]');
        if (label) label.textContent = 'Try again';
      });
      document.dispatchEvent(
        new CartErrorEvent(product.id, error instanceof Error ? error.message : 'Add to cart failed', {}, {})
      );
    } finally {
      setTimeout(() => {
        const currentVariant = findVariant();
        buttons.forEach((el) => {
          const btn = /** @type {HTMLButtonElement} */ (el);
          btn.disabled = !currentVariant?.available;
          const label = /** @type {HTMLElement | null} */ (btn.querySelector('[data-flv-add-label]'));
          if (label) label.textContent = label.dataset.originalText || 'Add to cart';
        });
      }, 1400);
    }
  });

  render();
}
